import { Injectable } from '@angular/core';

import { Subscription, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

import { AbstractHypeService } from '../../../Common/Layout/services/abstract-hype.service';
import { HypeModel } from '../../../Common/Layout/models/hype.model';
import { DiscoverRoutingService } from '../../DiscoverRouter/services/discover-routing.service';
import { DiscoverRouteModel } from '../../DiscoverRouter/models/discover-route.model';
import { DiscoverRouteTypeEnum } from '../../DiscoverRouter/enums/discover-route-type.enum';
import { TextInputDialogModel } from '../../../Common/Dialogs/models/text-input-dialog.model';
import { TargetingListService } from '../../Targeting/services/targeting-list.service';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { TextInputDialogComponent } from '../../../Common/Dialogs/components/TextInputDialog/text-input-dialog.component';
import { TargetingList } from '../../Targeting/models/targeting-list.model';
import { ViAgentDialogComponent } from '../../../VisibilityIntelligence/components/ViAgentDialog/vi-agent-dialog.component';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../Core/Dialogs/enums/overlay-animation.enum';

enum HypeType {
    savedSearches,
    targetedLists,
    distributions
}

@Injectable()
export class DiscoverHypeService extends AbstractHypeService {

    currentRoute: DiscoverRouteModel;

    protected hypeConfig: HypeModel;

    private hypeType: HypeType;
    private subscriptions: Subscription[] = [];

    constructor(
        private coreDialogService: CoreDialogService,
        private discoverRoutingService: DiscoverRoutingService,
        private targetingListService: TargetingListService
    ) {
        super();
        this.subscriptions.push(
            this.discoverRoutingService.getCurrentRoute().subscribe(
                route => {
                    this.currentRoute = route;
                    this.initializeContactsHype();
                }
            )
        );
    }

    private initializeContactsHype() {
        switch (this.currentRoute.type) {
            case DiscoverRouteTypeEnum.targetingSearchHype:
                this.hypeConfig = {
                    icon: 'fa-kit-duotone fa-agpr-hype-discover',
                    headingText: 'Discover.hype.savedSearches.headline',
                    line1Text: 'Discover.hype.savedSearches.line1',
                    line2Text: 'Discover.hype.savedSearches.line2',
                    linkText: 'Discover.hype.savedSearches.link'
                };
                this.hypeType = HypeType.savedSearches;
                break;
            case DiscoverRouteTypeEnum.targetingListHype:
                this.hypeConfig = {
                    icon: 'fa-kit-duotone fa-agpr-hype-discover',
                    headingText: 'Discover.hype.targetedLists.headline',
                    line1Text: 'Discover.hype.targetedLists.line1',
                    line2Text: 'Discover.hype.targetedLists.line2',
                    linkText: 'Discover.hype.targetedLists.link'
                };
                this.hypeType = HypeType.targetedLists;
                break;
            case DiscoverRouteTypeEnum.distributionHype:
                this.hypeConfig = {
                    icon: 'fa-kit-duotone fa-agpr-hype-distribution',
                    headingText: 'Discover.hype.distributions.headline',
                    line1Text: 'Discover.hype.distributions.line1',
                    line2Text: 'Discover.hype.distributions.line2',
                    linkText: 'Discover.hype.distributions.link'
                };
                this.hypeType = HypeType.distributions;
                break;
            default:
                break;
        }
        this.hypeConfigProvider.next(this.hypeConfig);
    }

    buttonClicked(): void {
        switch (this.hypeType) {
            case HypeType.savedSearches:
                this.discoverRoutingService.gotoTargetingSearch();
                break;
            case HypeType.targetedLists:
                this.createNewList();
                break;
            case HypeType.distributions:
                this.discoverRoutingService.goToNewDistribution();
                break;
            default:
                break;
        }
    }

    /** Not implemented */
    secondaryButtonClicked() { }


    private createNewList() {
        const io: TextInputDialogModel = {
            inputs: {
                heading: 'Discover.dialogs.newList.heading',
                description: 'Discover.dialogs.newList.description',
                placeholder: 'Discover.dialogs.newList.placeholder',
                okLabel: 'Discover.dialogs.newList.createList',
                cancelLabel: 'Discover.dialogs.universal.cancelLabel',
                width: 300,
                disabledButtonTooltip: 'Discover.dialogs.newList.listNameRequired',
                submit: (listName: string) => {
                    const newList = new TargetingList({name: listName});
                    return this.targetingListService.create(newList);
                },
                observableFormValidator: (listName: string) => {
                    return this.targetingListService.isNameDuplicated$(listName).pipe(
                        map((isDuplicate) => {
                            if (isDuplicate) {
                                return ['Discover.dialogs.newList.duplicate'];
                            } else {
                                return [];
                            }
                        })
                    );
                }
            },
            outputs: {}
        };
        this.coreDialogService.open(TextInputDialogComponent, io);
    }
}
